import { Link } from 'react-router-dom';
import { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link to={`/article/${article.id}`} className="group block cursor-pointer">
        <article className="relative overflow-hidden rounded-lg h-[420px] md:h-[480px]">
          <img
            src={article.coverImage}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-3">
              {article.isNew && (
                <span className="px-2 py-0.5 text-xs font-medium bg-primary-500 text-background-50 rounded">最新</span>
              )}
              <span className="px-2 py-0.5 text-xs font-medium bg-background-50/20 text-background-50 rounded backdrop-blur-sm">
                {article.category}
              </span>
            </div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-background-50 mb-2 line-clamp-2 group-hover:text-primary-300 transition-colors">
              {article.title}
            </h2>
            <p className="text-sm text-background-200/80 line-clamp-2 mb-3">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 text-xs text-background-300">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line text-xs w-3 h-3 flex items-center justify-center"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line text-xs w-3 h-3 flex items-center justify-center"></i>
                {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-message-2-line text-xs w-3 h-3 flex items-center justify-center"></i>
                {article.comments}
              </span>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link to={`/article/${article.id}`} className="group flex gap-4 cursor-pointer">
        <article className="flex gap-4">
          <div className="w-24 h-24 md:w-28 md:h-28 flex-shrink-0 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-sm text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors mb-1.5">
              {article.title}
            </h3>
            <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{article.excerpt}</p>
            <div className="flex items-center gap-3 text-xs text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
              </span>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link to={`/article/${article.id}`} className="group block cursor-pointer">
      <article className="bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-colors">
        <div className="aspect-[16/10] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="p-4 md:p-5">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded">
              {article.category}
            </span>
            {article.isHot && (
              <span className="px-2 py-0.5 text-xs font-medium bg-accent-100 text-accent-700 rounded">热门</span>
            )}
            {article.isNew && (
              <span className="px-2 py-0.5 text-xs font-medium bg-accent-100 text-accent-700 rounded">最新</span>
            )}
          </div>
          <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors mb-2">
            {article.title}
          </h3>
          <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center justify-between text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}